import React, { CSSProperties, useContext, useEffect, useRef, useState, useCallback, useMemo } from "react"
import { CampProps, ReservationProps } from "../../interfaces"
import { RESERVATION_KINDS, STATUSES, reservationStatus } from "~/constants"
import classNames from "../../utils/classNames"
import {
  ArrowPathRoundedSquareIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  NoSymbolIcon,
  CurrencyDollarIcon,
  MapPinIcon,
  CalendarDaysIcon,
  DocumentCheckIcon,
  DocumentMinusIcon,
} from "@heroicons/react/24/outline"
import {
  BuildingLibraryIcon,
  SparklesIcon,
  CheckBadgeIcon as CheckBadgeIconSolid,
  PlusCircleIcon,
} from "@heroicons/react/24/solid"
import moment from "moment"
import { UserGroupIcon, CreditCardIcon, DocumentTextIcon, LinkIcon, XMarkIcon } from "@heroicons/react/20/solid"
import { ReservationContextMenuContext } from "~/contexts/admin/ReservationContextMenuContext"
import AirBnb from "images/vendor_icons/Airbnb.svg?react"
import Booking from "images/vendor_icons/booking.svg?react"
import Hipcamp from "images/vendor_icons/hipcamp.svg?react"
import Vrbo from "images/vendor_icons/vrbo.svg?react"
import ArrowLeftOnRectangleIconSolid from "images/park__icon_ux__check_out.svg"
import CAMP_SLUGS_WITH_CAMPER_COMPANY_NAME_FIELD from "../../constants/camp_slugs_with_company_name_field"
import { CalendarApi } from "@fullcalendar/core"
import { formatDateRange } from "~/utils/formatDateRange"
import ProfileCard from "../Admin/Campers/Camper/ProfileCard"
import {
  StatusBlocked,
  StatusDeleted,
  StatusImported,
  StatusLocked,
  StatusPaid,
  StatusUnpaid,
} from "../ui/StatusBadges"
import { formatMoneyFromCents } from "~/utils/formatMoney"
import PortalHoverTip from "../ui/PortalHoverTip"
import useBreakpoint from "~/hooks/useBreakpoint"
import LockIconOutline from "~/icon/LockIconOutline"
import { isMobile, isTablet } from "react-device-detect"
import ICamper from "~/interfaces/ICamper"

interface ReservationStateProps {
  persisted: ReservationProps
  updated?: ReservationProps
}

interface ComponentProps {
  camp: CampProps
  reservations: { [reservationId: string]: ReservationStateProps } | undefined
  eventInfo: any
  setSelected: (reservation: ReservationProps) => void
  isSelected: boolean
  calendarApi: CalendarApi | undefined
  isQuarterlyView: boolean
}

const EventButton = (props: ComponentProps) => {
  const { showContextMenu } = useContext(ReservationContextMenuContext)
  const containerRef = useRef<HTMLDivElement>()
  const textContentRef = useRef<HTMLDivElement>()
  const [isSmall, setIsSmall] = useState(false)
  const [isOverflow, setIsOverflow] = useState(false)
  const event = props.eventInfo.event
  const reservationState: ReservationStateProps = props.reservations[event.id]
  const persistedReservation = reservationState?.persisted
  const [isChanged, setIsChanged] = useState(reservationState?.updated != null)
  const isMobile = useBreakpoint("(max-width: 768px)")
  const storeProducts = persistedReservation.order?.line_items

  const reservation: ReservationProps = reservationState?.updated || reservationState?.persisted

  useEffect(() => {
    setIsChanged(reservationState.updated != null)
  }, [reservationState.updated])

  const isUsingCompanyName = CAMP_SLUGS_WITH_CAMPER_COMPANY_NAME_FIELD.includes(props.camp.slug)
  const companyName = persistedReservation?.camper?.company_name
  const { email, first_name, last_name } = persistedReservation?.camper || {}
  const eventTitle =
    isUsingCompanyName && companyName
      ? companyName
      : last_name && first_name
        ? `${last_name}, ${first_name}`
        : last_name || first_name || email
  const amountOfNights = moment(persistedReservation.end_date).diff(persistedReservation.start_date, "days")

  const changeBlockedImporteStatus = () =>
    ({
      [props.eventInfo.event.id]: {
        persisted: {
          ...reservationState.persisted,
          status: "blocked-import",
        },
        updated: reservationState?.updated
          ? {
              ...reservationState.updated,
              status: "blocked-import",
            }
          : null,
      },
    }) as { [reservationId: string]: ReservationStateProps }

  const buttonStylesFor = (reservation: ReservationProps): CSSProperties => {
    return {
      background: isChanged ? "white" : eventColorFor(reservation, true),
      boxShadow: `0 0 0 1px ${eventColorFor(reservation, false)} inset`,
      color: isChanged ? "black" : "white",
      border: eventBorder(reservation.status),
      outlineOffset: props.isSelected ? "0px" : undefined,
    }
  }

  const importKindStyle = (kind?: string) => {
    switch (kind) {
      case "airbnb":
        return {
          color: `#FF6A86`,
          blockedColor: `repeating-linear-gradient(45deg, #fff, #fff 5px, #FFB0B8 5px, #FFB0B8 10px)`,
          blockedBorder: "1px solid #FD4D5D",
          image: AirBnb,
        }
      case "hipcamp":
        return {
          color: `#FD4D5D`,
          blockedColor: `repeating-linear-gradient(45deg, #fff, #fff 5px, #FFB0B8 5px, #FFB0B8 10px)`,
          blockedBorder: "1px solid #FD4D5D",
          image: Hipcamp,
        }
      case "booking":
        return {
          color: `#00ADF4`,
          blockedColor: `repeating-linear-gradient(45deg, #fff, #fff 5px, #84CFEE 5px, #84CFEE 10px)`,
          blockedBorder: "1px solid #00ADF4",
          image: Booking,
        }
      case "vrbo":
        return {
          color: `#457EC1`,
          blockedColor: `repeating-linear-gradient(45deg, #fff, #fff 5px, #76A2D5 5px, #76A2D5 10px)`,
          blockedBorder: "1px solid #457EC1",
          image: Vrbo,
        }
      default:
        return {
          color: `#FD4D5D`,
          blockedColor: `repeating-linear-gradient(45deg, #fff, #fff 5px, #FFB0B8 5px, #FFB0B8 10px)`,
          blockedBorder: "1px solid #FD4D5D",
          image: Hipcamp,
        }
    }
  }

  const eventBorder = (status: reservationStatus): string => {
    if (status === STATUSES.BLOCKED) {
      return "1px solid #CDCDCD"
    } else if (reservation.kind === RESERVATION_KINDS.FINAL_INVOICE) {
      return "1px solid #508548"
    } else if (status === STATUSES.PAID) {
      return "1px solid #78AC6B"
    } else if (status === STATUSES.UNPAID) {
      return "1px solid #AAD09F"
    } else if (status === STATUSES.IMPORTED) {
      return "1px solid #fff"
    } else if (status === STATUSES.BLOCKED_IMPORT) {
      return importKindStyle(
        reservation.site?.vendor_calendars?.find((calendar) => calendar.id === reservation.vendor_calendar_id)?.kind,
      ).blockedBorder
    }
  }

  const eventColorFor = (reservation: ReservationProps, background?: boolean): string => {
    switch (reservation.status) {
      case STATUSES.IMPORTED:
        return importKindStyle(
          reservation.site?.vendor_calendars?.find((calendar) => calendar.id === reservation.vendor_calendar_id)?.kind,
        ).color
      case STATUSES.BLOCKED_IMPORT:
        return importKindStyle(
          reservation.site?.vendor_calendars?.find((calendar) => calendar.id === reservation.vendor_calendar_id)?.kind,
        ).blockedColor
      case STATUSES.BLOCKED:
        return `repeating-linear-gradient(45deg, #fff, #fff 5px, #CDCDCD 5px, #CDCDCD 10px)`
      case STATUSES.PAID:
        return "#81C87E"
      case STATUSES.UNPAID:
        if (reservation.has_pending_payments) {
          return `repeating-linear-gradient(45deg, #fff, #fff 5px, #81C87E 5px, #E0F6DB 10px)`
        } else {
          const percentPaid = (reservation.total_paid_in_cents / reservation.total_owed_in_cents) * 100
          if (percentPaid > 0 && background)
            return `linear-gradient(to right,#81C87E 0%,#81C87E ${percentPaid}%,#E0F6DB ${percentPaid}%, #E0F6DB 100%)`
          else return "#E0F6DB"
        }
      default:
        return "grey"
    }
  }
  let PrimaryIcon = DocumentTextIcon

  if (persistedReservation.grouped_reservation_id) {
    PrimaryIcon = UserGroupIcon
  } else if (persistedReservation.status === "blocked") {
    PrimaryIcon = NoSymbolIcon
  } else if (persistedReservation.status === "blocked-import") {
    PrimaryIcon = importKindStyle(
      reservation.site?.vendor_calendars?.find((calendar) => calendar.id === reservation.vendor_calendar_id)?.kind,
    ).image
  } else if (persistedReservation.kind === "recurring") {
    PrimaryIcon = ArrowPathRoundedSquareIcon
  } else if (persistedReservation.stripe_payment_link_admin_url) {
    PrimaryIcon = LinkIcon
  } else if (persistedReservation.vendor_id) {
    if (persistedReservation.is_manual_charge) {
      PrimaryIcon = CreditCardIcon
    } else {
      PrimaryIcon = GlobeAltIcon
    }
  }

  const isHourlyReservation = () =>
    moment(persistedReservation.end_date).diff(persistedReservation.start_date, "hours") <= 12

  const isShortReservationInQuarterlyView = () => {
    if (!props.calendarApi?.view?.type) return false

    if (
      props.calendarApi.view.type === "resourceTimelineQuarterly" &&
      (amountOfNights < 2 ||
        moment(persistedReservation.end_date).diff(moment(props.calendarApi?.view?.activeStart), "days") <= 1)
    ) {
      return true
    }

    return false
  }

  useEffect(() => {
    if (isShortReservationInQuarterlyView() || isHourlyReservation()) {
      setIsSmall(true)
    } else {
      setIsSmall(false)
    }

    if (
      props.calendarApi?.view?.currentStart &&
      moment(persistedReservation.end_date).diff(moment(props.calendarApi.view.currentStart), "days") < 1
    ) {
      setIsOverflow(true)
    } else {
      setIsOverflow(false)
    }
  }, [props.calendarApi?.view?.type])

  const handleContextMenu = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault()
    showContextMenu(reservationState, e)
  }
  const [showEventImage, setShowEventImage] = useState(true)
  const calculateDifferenceInDaysForQuarterly = () => {
    const differenceInDays = moment(reservation?.end_date).diff(
      moment(props?.calendarApi?.view.activeStart),
      "days",
      true,
    )
    if (differenceInDays <= 1 && props?.isQuarterlyView && reservation.kind !== RESERVATION_KINDS.FINAL_INVOICE) {
      showEventImage && setShowEventImage(false)
      if (reservation?.status === "blocked-import") return "small-bottom-import"
      else return "small-top-import"
    }

    return ""
  }

  const calculateLeftPosition = () => {
    const quarterlyOffset = props.isQuarterlyView ? -1 : -1
    const basePosition = isSmall ? 1 : -1
    const orderProductsOffset = Array.isArray(storeProducts) && storeProducts?.length > 0 ? -15 : 0
    const lockedOffset = reservation.is_locked ? -10 : -5
    const isLockedAndOrderProducts =
      reservation.is_locked && Array.isArray(storeProducts) && storeProducts.length > 0 ? 12 : 0
    return `${basePosition + orderProductsOffset + lockedOffset + quarterlyOffset + isLockedAndOrderProducts}px`
  }

  const HoverCard = useMemo(() => {
    return (
      <div className="space-y-2 py-3 px-2">
        <div>
          <div className="flex items-stretch justify-between mb-6 gap-4">
            <div className="min-w-0 w-full max-w-[324px]">
              <ProfileCard
                camper={reservation?.camper as ICamper}
                hideAdditionalInfo
                hidePaymentMethods
                hideCardBorder
                cardContentWidth={"max-w-[244px] w-full"}
              />
            </div>
            <div className="rounded-md py-auto flex items-center ml-5 gap-4 self-stretch">
              <div className="flex flex-col w-full">
                <div className="text-gray-400 font-semibold text-sm whitespace-nowrap text-nowrap">Total Owed</div>
                <div className="text-base font-medium text-black">
                  {formatMoneyFromCents(
                    reservation.total_owed_in_cents ?? reservation.order?.unpaid_total_in_cents ?? 0,
                    props.camp.currency,
                  )}
                </div>
              </div>
              <div className="flex flex-col w-full">
                <div className="text-gray-400 font-semibold text-sm whitespace-nowrap text-nowrap">Total Paid</div>
                <div className="text-base font-medium text-black">
                  {formatMoneyFromCents(
                    reservation.total_paid_in_cents ?? reservation.order?.paid_total_in_cents ?? 0,
                    props.camp.currency,
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center mt-2 whitespace-nowrap flex-wrap gap-x-4 gap-y-2">
          <div className="flex items-center text-gray-600 max-w-full pl-1">
            <MapPinIcon className="w-5 h-5 text-gray-600 mr-1" />
            <div className="font-medium truncate">{reservation?.site.name}</div>
          </div>
          <div className="flex items-center text-gray-600">
            <CalendarDaysIcon className="w-5 h-5 text-gray-600 mr-1 " />
            <div className="">
              <span className="font-medium">{formatDateRange(reservation?.start_date, reservation?.end_date)}</span>
            </div>
          </div>
          <div className="font-medium">
            {reservation?.status === STATUSES.UNPAID ? (
              <StatusUnpaid />
            ) : reservation?.status === STATUSES.PAID ? (
              <StatusPaid refundedAmountInCents={reservation?.order?.amount_refunded_in_cents} />
            ) : [STATUSES.IMPORTED, "blocked-import"].includes(reservation?.status) ? (
              <StatusImported />
            ) : (
              <StatusBlocked />
            )}
          </div>
          {reservation.is_locked && (
            <div className="flex items-center flex-wrap px-1" data-testid="status-locked">
              <LockIconOutline className="w-5 h-5 mr-1" data-testid="lock-icon" />
              <span>Reservation locked</span>
            </div>
          )}
          {props.camp.tos_requires_signature && (
            <>
              {moment(reservation?.tos_agreed_at).isValid() ? (
                <>
                  <div className="flex gap-2 items-center">
                    {moment(reservation?.tos_agreed_at).isValid() ? (
                      <DocumentCheckIcon className="text-gray-600 font-medium h-5 w-5" />
                    ) : (
                      <DocumentMinusIcon className="text-gray-600 font-medium h-5 w-5" />
                    )}
                    <div className="font-medium text-gray-600">Rules</div>
                  </div>

                  <div className="flex gap-2 items-center md:hidden">
                    {moment(reservation?.tos_agreed_at).isValid() ? (
                      <DocumentCheckIcon className="text-gray-600 font-medium h-5 w-5" />
                    ) : (
                      <DocumentMinusIcon className="text-gray-600 font-medium h-5 w-5" />
                    )}
                    <div className="font-medium text-gray-600">Rules</div>
                  </div>
                </>
              ) : (
                <div className="flex gap-2 items-center">
                  {moment(reservation?.tos_agreed_at).isValid() ? (
                    <DocumentCheckIcon className="text-gray-600 font-medium h-5 w-5" />
                  ) : (
                    <DocumentMinusIcon className="text-gray-600 font-medium h-5 w-5" />
                  )}
                  <div className="font-medium text-gray-600">Rules</div>
                </div>
              )}
            </>
          )}
          {moment().diff(moment(reservation?.created_at), "hours") <= 24 &&
            reservation?.vendor_id &&
            !reservation?.is_manual_charge && (
              <div className="flex gap-2 items-center">
                <SparklesIcon className="text-yellow-400 font-medium h-5 w-5" />
                <div className="font-medium text-gray-600">New booking</div>
              </div>
            )}
          {Array.isArray(storeProducts) && storeProducts.length > 0 && (
            <div className="flex w-full flex-col px-1 py-0.5">
              <span className="flex items-center gap-1">
                <PlusCircleIcon className="w-5 h-5 " />
                Products
              </span>
              <div className="flex gap-2 flex-wrap w-full">
                {storeProducts.map((addon, idx, array) => (
                  <div key={idx} className="flex items-center space-x-1">
                    <span>{addon.label}</span>
                    <span className="font-semibold">x {addon.quantity}</span>
                    {idx < array.length - 1 && <span>,</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="">
          {reservation?.long_term_reservation?.is_enabled_auto_charging &&
            props.camp.is_enabled_auto_charging == true && (
              <span className="mt-2 inline-flex items-center rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700">
                Autocharge enabled
              </span>
            )}
        </div>
        <div className="flex justify-between mt-4 text-sm text-gray-500">
          <span className="mr-4 italic">
            Created {moment(reservation?.created_at).format(`${props.camp.date_format} h:mma`)}
          </span>
          <span className="italic">Last edited {moment(reservation?.updated_at).fromNow()}</span>
        </div>
      </div>
    )
  }, [reservation])

  const [isMounted, setIsMounted] = useState(false)
  useEffect(() => {
    setIsMounted(true)
  }, [])

  return (
    <>
      {isMounted && (
        <PortalHoverTip
          content={HoverCard}
          dark={false}
          maxWidth="524px"
          active={
            !isMobile &&
            !isTablet &&
            reservation.status !== STATUSES.BLOCKED &&
            reservation.status !== STATUSES.IMPORTED &&
            reservation.status !== STATUSES.BLOCKED_IMPORT
          }
          followMouse
          delayIn={500}
          showArrow={false}
        >
          <div
            style={buttonStylesFor(persistedReservation)}
            className={classNames(
              `${
                reservation.status === "blocked-import" ? "blocked-import" : "event-button"
              } items-center hover:opacity-90 rounded-lg`,
              !persistedReservation.is_locked && "cursor-pointer",
              props.calendarApi?.view?.type === "resourceTimelineQuarterly" &&
                moment(persistedReservation.end_date).dayOfYear() ==
                  moment(props.calendarApi?.view?.activeStart).dayOfYear()
                ? "absolute z-20"
                : "flex",
              "transition-opacity duration-300",
              props.isSelected && " outline-black",
              isSmall
                ? `justify-center${persistedReservation.status !== STATUSES.BLOCKED && " px-1.125"}`
                : `justify-start${persistedReservation.status !== STATUSES.BLOCKED && " px-1.5"}`,
              reservation.is_locked && "border-1 border-gray-300",
              calculateDifferenceInDaysForQuarterly(),
              "group relative",
            )}
            onClick={() => {
              props.setSelected(persistedReservation)
            }}
            onContextMenu={handleContextMenu}
            data-testid={`calendar-button-event-${event?._def?.defId}${persistedReservation?.status === STATUSES.BLOCKED && "-blocked"}`}
          >
            {persistedReservation.status !== STATUSES.BLOCKED && (
              <div className="flex-grow flex-shrink min-w-0" ref={containerRef}>
                <div className="sticky left-6 right-6 flex items-center">
                  {persistedReservation.is_checked_in && !persistedReservation.is_checked_out && (
                    <div
                      className="absolute -top-3 rounded-full bg-white z-50 text-[#3A8860]"
                      style={{
                        left: "-15px",
                      }}
                    >
                      <CheckBadgeIconSolid className="w-5.5 h-5.5" data-testid="checked-in-icon" />
                    </div>
                  )}
                  {persistedReservation.is_checked_out && (
                    <div
                      className="absolute -top-3 rounded-full bg-white"
                      style={{
                        left: "-15px",
                      }}
                    >
                      <img src={ArrowLeftOnRectangleIconSolid} className="w-5.5 h-5.5" data-testid="checked-out-icon" />
                    </div>
                  )}
                  <div
                    className={classNames("absolute top-3.5 z-10", (reservation.is_locked && "w-5.5") || "w-11")}
                    style={{ left: calculateLeftPosition() }}
                  >
                    {Array.isArray(storeProducts) && storeProducts.length > 0 && (
                      <span className="absolute left-3">
                        <span className="rounded-full bg-white w-5.5 h-5.5 flex items-center justify-center border border-gray-300">
                          <PlusCircleIcon className="h-5.5 w-5.5 text-green-600" />
                        </span>
                      </span>
                    )}
                    <span className="absolute top-0 flex items-center justify-center">
                      {reservation.is_locked && (
                        <span className="rounded-full bg-white w-5.5 h-5.5 flex items-center justify-center border border-gray-300">
                          <span className="rounded-full bg-gray-500 text-white w-4.5 h-4.5 flex items-center justify-center">
                            <LockIconOutline className="w-3.5 h-3.5" data-testid="lock-icon" />
                          </span>
                        </span>
                      )}
                    </span>
                  </div>
                  {moment().diff(moment(reservation?.created_at), "hours") <= 24 &&
                    reservation?.vendor_id &&
                    !reservation?.is_manual_charge && (
                      <div
                        className="absolute bottom-3.5 rounded-full bg-white"
                        style={{
                          right: "-15px",
                        }}
                      >
                        <div className="rounded-full bg-white w-5.5 h-5.5 flex items-center justify-center">
                          <div className="rounded-full bg-yellow-400 text-white w-4.5 h-4.5 flex items-center justify-center">
                            <SparklesIcon className="w-3 h-3" />
                          </div>
                        </div>
                      </div>
                    )}
                  {PrimaryIcon && showEventImage && (
                    <PrimaryIcon
                      style={reservation.status && { width: 17, height: 17 }}
                      className={classNames("w-4.5 h-4.5 flex-shrink-0 text-black", !isSmall && "mr-1.5")}
                    />
                  )}
                  {!isSmall && !isOverflow && (
                    <>
                      {persistedReservation.invoice_sent_at && (
                        <EnvelopeIcon className="w-4 h-4 flex-shrink-0 mr-1.5 text-black" />
                      )}
                      {persistedReservation.long_term_reservation &&
                        persistedReservation.long_term_reservation.is_enabled_auto_charging == true && (
                          <CurrencyDollarIcon className="w-4 h-4 flex-shrink-0 mr-1.5 text-black" />
                        )}
                      {persistedReservation.has_pending_payments && persistedReservation.status != STATUSES.PAID && (
                        <BuildingLibraryIcon className="w-4 h-4 flex-shrink-0 mr-1.5 text-black" />
                      )}
                      <div
                        ref={textContentRef}
                        className="truncate text-gray-800 text-baselg font-medium"
                        style={{
                          lineHeight: "19px",
                        }}
                      >
                        {eventTitle}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
          {reservation.status === "imported" && (
            <EventButton
              camp={props.camp}
              reservations={changeBlockedImporteStatus()}
              eventInfo={props.eventInfo}
              isSelected={props.isSelected}
              setSelected={props.setSelected}
              calendarApi={props.calendarApi}
              isQuarterlyView={props.isQuarterlyView}
            />
          )}
        </PortalHoverTip>
      )}
    </>
  )
}

export default EventButton
